import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiPlus, FiEdit, FiTrash2, FiX, FiKey, FiSlash, FiCheckCircle, FiCopy, FiUserCheck, FiSettings, FiBarChart2, FiMail } from 'react-icons/fi';
import { showNotification } from './Notification';
import ConfirmDialog from './ConfirmDialog';
import AssessmentReport from './AssessmentReport';
import './Assessment.css';

const emptyForm = { surname: '', first_name: '', other_name: '', specialization: '', email: '', phone: '', batch_ids: [] };

const Assessment = () => {
  const [assessors, setAssessors] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [codeModal, setCodeModal] = useState({ open: false, name: '', code: '', delivery: null });

  const [unitsCount, setUnitsCount] = useState(12);
  const [unitsInput, setUnitsInput] = useState('12');
  const [savingUnits, setSavingUnits] = useState(false);

  const [emailProvider, setEmailProvider] = useState('auto');
  const [smsProvider, setSmsProvider] = useState('auto');
  const [providerOptions, setProviderOptions] = useState({ email: [], sms: [] });
  const [savingProviders, setSavingProviders] = useState(false);

  const [activeTab, setActiveTab] = useState('setup');

  useEffect(() => {
    fetchAssessors();
    fetchSpecializations();
    fetchBatches();
    fetchUnitsSetting();
    fetchProviderOptions();
  }, []);

  const fetchBatches = async () => {
    try {
      const res = await axios.get('/api/batches');
      setBatches(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setBatches([]);
    }
  };

  const fetchUnitsSetting = async () => {
    try {
      const res = await axios.get('/api/assessment-settings');
      setUnitsCount(res.data.units_count);
      setUnitsInput(String(res.data.units_count));
      setEmailProvider(res.data.notify_email_provider || 'auto');
      setSmsProvider(res.data.notify_sms_provider || 'auto');
    } catch (error) {
      // Non-fatal — falls back to the defaults shown in state
    }
  };

  const fetchProviderOptions = async () => {
    try {
      const res = await axios.get('/api/assessment-settings/providers');
      setProviderOptions({ email: res.data.email || [], sms: res.data.sms || [] });
    } catch (error) {
      setProviderOptions({ email: [], sms: [] });
    }
  };

  const handleSaveProviders = async (e) => {
    e.preventDefault();
    setSavingProviders(true);
    try {
      const res = await axios.put('/api/assessment-settings', {
        notify_email_provider: emailProvider,
        notify_sms_provider: smsProvider,
      });
      setEmailProvider(res.data.notify_email_provider);
      setSmsProvider(res.data.notify_sms_provider);
      showNotification('Notification providers saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error saving providers', 'error');
    } finally {
      setSavingProviders(false);
    }
  };

  const handleSaveUnits = async (e) => {
    e.preventDefault();
    const value = parseInt(unitsInput, 10);
    if (!Number.isInteger(value) || value < 1 || value > 52) {
      showNotification('Units must be a whole number between 1 and 52', 'error');
      return;
    }
    setSavingUnits(true);
    try {
      const res = await axios.put('/api/assessment-settings', { units_count: value });
      setUnitsCount(res.data.units_count);
      setUnitsInput(String(res.data.units_count));
      showNotification(`Assessors will now see Unit 1–${res.data.units_count}`, 'success');
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error saving units', 'error');
    } finally {
      setSavingUnits(false);
    }
  };

  const fetchAssessors = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/assessors');
      setAssessors(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error fetching assessors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecializations = async () => {
    try {
      const res = await axios.get('/api/assessors/specializations');
      setSpecializations(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setSpecializations([]);
    }
  };

  const fullName = (a) => [a.surname, a.first_name, a.other_name].filter(Boolean).join(' ');

  const handleAdd = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleEdit = (a) => {
    setEditing(a);
    setFormData({
      surname: a.surname || '',
      first_name: a.first_name || '',
      other_name: a.other_name || '',
      specialization: a.specialization || '',
      email: a.email || '',
      phone: a.phone || '',
      batch_ids: Array.isArray(a.batches) ? a.batches.map((b) => b.id) : [],
    });
    setShowModal(true);
  };

  const toggleBatch = (batchId) => {
    setFormData((prev) => {
      const has = prev.batch_ids.includes(batchId);
      return {
        ...prev,
        batch_ids: has ? prev.batch_ids.filter((id) => id !== batchId) : [...prev.batch_ids, batchId],
      };
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData(emptyForm);
  };

  const closeCodeModal = () => setCodeModal({ open: false, name: '', code: '', delivery: null });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.surname.trim() || !formData.first_name.trim() || !formData.specialization.trim() || !formData.email.trim()) {
      showNotification('Surname, first name, specialization and email are required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/assessors/${editing.id}`, formData);
        showNotification('Assessor updated', 'success');
      } else {
        await axios.post('/api/assessors', formData);
        showNotification('Assessor created', 'success');
      }
      closeModal();
      fetchAssessors();
      fetchSpecializations();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error saving assessor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (a) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Assessor',
      message: `Delete ${fullName(a)}? This cannot be undone.`,
      onConfirm: async () => {
        setBusyId(a.id);
        try {
          await axios.delete(`/api/assessors/${a.id}`);
          showNotification('Assessor deleted', 'success');
          fetchAssessors();
        } catch (error) {
          showNotification(error.response?.data?.error || 'Error deleting assessor', 'error');
        } finally {
          setBusyId(null);
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      },
    });
  };

  const handleGenerateCode = (a) => {
    const isReset = a.has_code;
    setConfirmDialog({
      isOpen: true,
      title: isReset ? 'Reset Access Code' : 'Generate Access Code',
      message:
        `${isReset ? 'Reset' : 'Generate'} the access code for ${fullName(a)}? ` +
        `A new code will be emailed to ${a.email}${a.phone ? ' and sent by SMS' : ''}. ` +
        `${isReset ? 'Their previous code will stop working.' : ''}`,
      onConfirm: async () => {
        setBusyId(a.id);
        try {
          const res = await axios.post(`/api/assessors/${a.id}/generate-code`);
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
          setCodeModal({ open: true, name: fullName(a), code: res.data.code, delivery: res.data.delivery });
          fetchAssessors();
        } catch (error) {
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
          showNotification(error.response?.data?.error || 'Error generating code', 'error');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const handleToggleRestrict = async (a) => {
    setBusyId(a.id);
    try {
      await axios.patch(`/api/assessors/${a.id}/restrict`, { is_restricted: !a.is_restricted });
      showNotification(a.is_restricted ? 'Assessor access enabled' : 'Assessor access restricted', 'success');
      fetchAssessors();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error updating access', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const copyCode = () => {
    if (codeModal.code && navigator.clipboard) {
      navigator.clipboard.writeText(codeModal.code);
      showNotification('Code copied', 'success');
    }
  };

  if (loading) {
    return <div className="loading">Loading assessors...</div>;
  }

  return (
    <div className="assessment">
      <div className="assessment-header">
        <div>
          <h1><FiUserCheck /> Assessment</h1>
          <p className="assessment-sub">Set up assessors, issue access codes, and control who can sign in.</p>
        </div>
        <button className="btn btn-primary" onClick={handleAdd}><FiPlus /> Add Assessor</button>
      </div>

      <div className="assessment-tabs">
        <button className={`tab-btn ${activeTab === 'setup' ? 'active' : ''}`} onClick={() => setActiveTab('setup')}>
          <FiUserCheck /> Assessors
        </button>
        <button className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`} onClick={() => setActiveTab('report')}>
          <FiBarChart2 /> Report
        </button>
      </div>

      {activeTab === 'report' ? (
        <AssessmentReport />
      ) : (
      <>
      <div className="assessment-settings-grid">
        <div className="settings-card">
          <h3><FiSettings /> Units</h3>
          <p className="settings-card-sub">Number of unit tabs every assessor sees (Unit 1–{unitsCount}).</p>
          <form className="units-form" onSubmit={handleSaveUnits}>
            <input
              type="number"
              min="1"
              max="52"
              value={unitsInput}
              onChange={(e) => setUnitsInput(e.target.value)}
              disabled={savingUnits}
            />
            <button type="submit" className="btn btn-primary" disabled={savingUnits}>
              {savingUnits ? 'Saving...' : 'Save'}
            </button>
          </form>
        </div>

        <div className="settings-card">
          <h3><FiMail /> Notification Providers</h3>
          <p className="settings-card-sub">Which provider sends the access-code email and SMS when an assessor is created or reset.</p>
          <form className="provider-form" onSubmit={handleSaveProviders}>
            <div className="form-group">
              <label>Email provider</label>
              <select value={emailProvider} onChange={(e) => setEmailProvider(e.target.value)} disabled={savingProviders}>
                {providerOptions.email.map((p) => (
                  <option key={p.value} value={p.value} disabled={!p.configured}>
                    {p.label}{!p.configured ? ' (not configured)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>SMS provider</label>
              <select value={smsProvider} onChange={(e) => setSmsProvider(e.target.value)} disabled={savingProviders}>
                {providerOptions.sms.map((p) => (
                  <option key={p.value} value={p.value} disabled={!p.configured}>
                    {p.label}{!p.configured ? ' (not configured)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn btn-primary" disabled={savingProviders}>
              {savingProviders ? 'Saving...' : 'Save'}
            </button>
          </form>
        </div>
      </div>

      {assessors.length === 0 ? (
        <div className="assessment-empty">
          <p>No assessors yet. Click <strong>Add Assessor</strong> to create your first one.</p>
        </div>
      ) : (
        <div className="assessment-table-wrap">
          <table className="assessment-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Specialization</th>
                <th>Batches</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Access code</th>
                <th>Status</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessors.map((a) => (
                <tr key={a.id} className={a.is_restricted ? 'restricted-row' : ''}>
                  <td data-label="Name">{fullName(a)}</td>
                  <td data-label="Specialization"><span className="chip">{a.specialization}</span></td>
                  <td data-label="Batches">
                    {Array.isArray(a.batches) && a.batches.length > 0 ? (
                      <div className="batch-chip-list">
                        {a.batches.map((b) => <span key={b.id} className="chip chip-muted">{b.name}</span>)}
                      </div>
                    ) : (
                      <span className="badge badge-muted">All (auto-shared)</span>
                    )}
                  </td>
                  <td data-label="Email">{a.email}</td>
                  <td data-label="Phone">{a.phone || '—'}</td>
                  <td data-label="Access code">
                    {a.has_code
                      ? <span className="badge badge-ok">Issued</span>
                      : <span className="badge badge-muted">Not issued</span>}
                  </td>
                  <td data-label="Status">
                    {a.is_restricted
                      ? <span className="badge badge-danger">Restricted</span>
                      : <span className="badge badge-ok">Active</span>}
                  </td>
                  <td data-label="Actions" className="actions-col">
                    <div className="row-actions">
                      <button className="btn-icon" title="Edit" disabled={busyId === a.id} onClick={() => handleEdit(a)}><FiEdit /></button>
                      <button className="btn-icon" title={a.has_code ? 'Reset access code' : 'Generate access code'} disabled={busyId === a.id} onClick={() => handleGenerateCode(a)}><FiKey /></button>
                      <button
                        className={`btn-icon ${a.is_restricted ? '' : 'warn'}`}
                        title={a.is_restricted ? 'Enable access' : 'Restrict access'}
                        disabled={busyId === a.id}
                        onClick={() => handleToggleRestrict(a)}
                      >
                        {a.is_restricted ? <FiCheckCircle /> : <FiSlash />}
                      </button>
                      <button className="btn-icon danger" title="Delete" disabled={busyId === a.id} onClick={() => handleDelete(a)}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Assessor' : 'Add Assessor'}</h2>
              <button className="close-btn" onClick={closeModal}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className="assessor-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Surname *</label>
                  <input type="text" value={formData.surname} onChange={(e) => setFormData({ ...formData, surname: e.target.value })} required disabled={saving} />
                </div>
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required disabled={saving} />
                </div>
              </div>
              <div className="form-group">
                <label>Other Name</label>
                <input type="text" value={formData.other_name} onChange={(e) => setFormData({ ...formData, other_name: e.target.value })} disabled={saving} />
              </div>
              <div className="form-group">
                <label>Specialization *</label>
                {specializations.length > 0 ? (
                  <select value={formData.specialization} onChange={(e) => setFormData({ ...formData, specialization: e.target.value })} required disabled={saving}>
                    <option value="">-- Select specialization --</option>
                    {specializations.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <input type="text" value={formData.specialization} onChange={(e) => setFormData({ ...formData, specialization: e.target.value })} placeholder="e.g., Poultry" required disabled={saving} />
                )}
                <small className="field-hint">Trainees with this exact specialization will be auto-assigned to this assessor.</small>
              </div>
              <div className="form-group">
                <label>Batches</label>
                {batches.length > 0 ? (
                  <div className="batch-checkbox-list">
                    {batches.map((b) => (
                      <label key={b.id} className="batch-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.batch_ids.includes(b.id)}
                          onChange={() => toggleBatch(b.id)}
                          disabled={saving}
                        />
                        {b.name}
                      </label>
                    ))}
                  </div>
                ) : (
                  <small className="field-hint">No batches exist yet.</small>
                )}
                <small className="field-hint">
                  {formData.batch_ids.length > 0
                    ? 'This assessor only manages trainees in the checked batches (within their specialization).'
                    : 'No batch checked — this assessor will auto-share the remaining trainees in their specialization not already claimed by a batch-restricted assessor.'}
                </small>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={saving} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="For SMS code delivery" disabled={saving} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {codeModal.open && (
        <div className="modal" onClick={closeCodeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Access Code — {codeModal.name}</h2>
              <button className="close-btn" onClick={closeCodeModal}><FiX /></button>
            </div>
            <div className="code-modal-body">
              <p>Share this code privately with the assessor. It won't be shown again — it's stored securely (hashed), so reset it if lost.</p>
              <div className="code-box">
                <span className="code-value">{codeModal.code}</span>
                <button className="btn-icon" title="Copy" onClick={copyCode}><FiCopy /></button>
              </div>
              {codeModal.delivery && (
                <ul className="delivery-status">
                  <li>Email: <strong>{codeModal.delivery.email}</strong></li>
                  <li>SMS: <strong>{codeModal.delivery.sms}</strong></li>
                </ul>
              )}
              <div className="form-actions">
                <button className="btn btn-primary" onClick={closeCodeModal}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
      />
      </>
      )}
    </div>
  );
};

export default Assessment;
